import React, { useContext, useState } from "react";
import {
    Loader,
} from "@fluentui/react-northstar";
import { SegmentContent } from "shared/components";
import NotesListWrapper from "./notes-list/NotesListWrapper";
import NoteDetailsForm, { NoteDetailsFormReadOnly } from "./note-details-form/NoteDetailsForm";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ResizerContext } from "shared/stores/resizeStore/ResizerContext";
import { constants } from "shared/constants";
import NotesService from "../service/NotesService";
import { cloneDeep } from "lodash";
import GenericError from "./errors/GenericError";
import { INoteDetails } from "../models";
import { CreateNewNoteRequest, UpdateNoteRequest } from "../service/contracts";
import NotesTabContent from "./NotesTabContent";
import { NoteSubjectTypeEnum } from "view360Card/models";
import { EntityNoteHeader_PublicEntityType, IntermediaryReference_Public } from "shared/service/api/generated";
import pl from "shared/messages/pl";

export interface View360NotesTabContentProps {
    salesPersonId?: number;
    intermediaryId?: number;
    intermediaryReference?: IntermediaryReference_Public[];
}

const View360NotesTabContent: React.FC<View360NotesTabContentProps> = ({
    salesPersonId,
    intermediaryId,
    intermediaryReference
}) => {
    const resizeContext = useContext(ResizerContext);
    const isMobile = resizeContext.windowWidth < constants.view360MobileDeviceWidthPx;
    const queryClient = useQueryClient();

    const [selectedNoteId, setSelectedNoteId] = useState<number>();
    const [entityType, setEntitytype] = useState<EntityNoteHeader_PublicEntityType>();
    const [entityId, setEntityId] = useState<number>();
    const [mobileFullScreenViewOpened, setMobileFullScreenViewOpened] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const [editableNoteDetails, setEditableNoteDetails] = useState<INoteDetails>({
        type: undefined,
        intermediaryReferenceList: [],
        contactDate: new Date(),
        subjectRetailList: [],
        subjectCorpoList: [],
        body: ""
    });

    const [entityName, setEntityName] = useState<string>("");

    const noteTypesQuery = useQuery("noteTypes", async () => {
        return await NotesService.getAvailableNoteTypes();
    });

    const noteTypeList = cloneDeep(noteTypesQuery.data);

    const noteSubjectsRetailQuery = useQuery("noteSubjects", async () => {
        return await NotesService.getAvailableNoteSubjects();
    });

    const noteAuthorsQuery = useQuery("noteAuthors", async () => {
        return await NotesService.getAvailableNoteAuthors();
    })

    const noteQuery = useQuery(["note", selectedNoteId], async () => {
        let note;

        if(selectedNoteId && entityId){
            if (entityType === EntityNoteHeader_PublicEntityType.OFWCA) {
                note = await NotesService.getSalesPersonNoteDetails(entityId, selectedNoteId);
            }

            if (entityType === EntityNoteHeader_PublicEntityType.INTERMEDIARY) {
                note = await NotesService.getIntermediaryNoteDetails(entityId, selectedNoteId);
            }
        }

        if (note) {
            setEditableNoteDetails({
                type: note.type,
                intermediaryReferenceList: note.intermediaryReferenceList,
                contactDate: note.contactDate,
                subjectRetailList: note.subjectRetailList,
                subjectCorpoList: note.subjectCorpoList,
                body: note.body
            })
            setEntityName(note.entityName);
        } else {
            setEditableNoteDetails({
                type: undefined,
                intermediaryReferenceList: [],
                contactDate: new Date(),
                subjectRetailList: [],
                subjectCorpoList: [],
                body: ""
            })
            setEntityName("");
        }

        setEditMode(false);

        return note;
    });

    const createNoteMutation = useMutation(
        async () => {
            if (!editableNoteDetails.type?.code) {
                throw new Error("Note type code is required.");
            }

            if (!editableNoteDetails.contactDate) {
                throw new Error("Contact date is required.");
            }

            const request: CreateNewNoteRequest = {
                noteTypeCode: editableNoteDetails.type.code,
                contactDate: editableNoteDetails.contactDate,
                subjectRetailCodes: editableNoteDetails.subjectRetailList.map((x) => x.code),
                subjectCorpoCodes: editableNoteDetails.subjectCorpoList.map((x) => x.code),
                body: editableNoteDetails.body,
                intermediaryReferenceList: intermediaryReference?.length === 1
                    ? intermediaryReference
                    : editableNoteDetails.intermediaryReferenceList,
            };

            let noteId;

            if (salesPersonId) {
                setEntitytype(EntityNoteHeader_PublicEntityType.OFWCA);
                setEntityId(salesPersonId);
                noteId = await NotesService.addSalesPersonNote(salesPersonId, request);
            }

            if (intermediaryId) {
                setEntitytype(EntityNoteHeader_PublicEntityType.INTERMEDIARY);
                setEntityId(intermediaryId);
                noteId = await NotesService.addIntermediaryNote(intermediaryId, request);
            }

            if (!noteId) {
                throw new Error("Note ID must not be undefined.");
            }

            return noteId;
        },
        {
            onSuccess: (data) => {
                setSelectedNoteId(data);
                queryClient.invalidateQueries("notes");
            },
        }
    );

    const updateNoteMutation = useMutation(
        async () => {
            if (!selectedNoteId) {
                throw new Error("Selected note ID is required.");
            }

            if (!editableNoteDetails.type?.code) {
                throw new Error("Note type code is required.");
            }

            if (!editableNoteDetails.contactDate) {
                throw new Error("Contact date is required.");
            }

            const request: UpdateNoteRequest = {
                noteTypeCode: editableNoteDetails.type.code,
                contactDate: editableNoteDetails.contactDate,
                subjectRetailCodes: editableNoteDetails.subjectRetailList.map((x) => x.code),
                subjectCorpoCodes: editableNoteDetails.subjectCorpoList.map((x) => x.code),
                body: editableNoteDetails.body,
                intermediaryReferenceList: intermediaryReference?.length === 1
                    ? intermediaryReference
                    : editableNoteDetails.intermediaryReferenceList,
            };

            let noteId;

            if (entityType === EntityNoteHeader_PublicEntityType.OFWCA && entityId) {
                noteId = await NotesService.updateSalesPersonNote(entityId, selectedNoteId, request);
            }

            if (entityType === EntityNoteHeader_PublicEntityType.INTERMEDIARY && entityId) {
                noteId = await NotesService.updateIntermediaryNote(entityId, selectedNoteId, request);
            }

            if (!noteId) {
                throw new Error(
                    "Note ID returned after update operation was different from currently selected note ID."
                );
            }

            return noteId;
        },
        {
            onSuccess: () => {
                noteQuery.refetch();
                queryClient.invalidateQueries("notes");
            }
        }
    );

    const deleteNoteMutation = useMutation(
        async () => {
            if (!selectedNoteId) {
                throw new Error("Selected note ID is required.");
            }

            if (entityType === EntityNoteHeader_PublicEntityType.OFWCA && entityId) {
                await NotesService.deleteSalesPersonNote(entityId, selectedNoteId);

            }

            if (entityType === EntityNoteHeader_PublicEntityType.INTERMEDIARY && entityId) {
                await NotesService.deleteIntermediaryNote(entityId, selectedNoteId);
            }
        },
        {
            onSuccess: () => {
                setSelectedNoteId(undefined);
                queryClient.invalidateQueries("notes");
            },
        }
    );

    if (noteSubjectsRetailQuery.isError) {
        return <GenericError message="Wystąpił problem podczas pobierania dostępnych tematów notatki pod detal." />;
    }

    if (noteAuthorsQuery.isError) {
        return <GenericError message="Wystąpił problem podczas pobierania dostępnych autorów notatek." />;
    }

    if (noteQuery.isError) {
        return <GenericError message="Wystąpił problem podczas pobierania notatki." />;
    }

    if (createNoteMutation.isError) {
        return <GenericError message="Wystąpił problem podczas tworzenia notatki." />;
    }

    if (updateNoteMutation.isError) {
        return <GenericError message="Wystąpił problem podczas aktualizacji notatki." />;
    }

    if (deleteNoteMutation.isError) {
        return <GenericError message="Wystąpił problem podczas usuwania notatki." />;
    }

    const loadingInProgress =
        noteSubjectsRetailQuery.isFetching ||
        noteQuery.isFetching ||
        noteAuthorsQuery.isFetching ||
        createNoteMutation.isLoading ||
        updateNoteMutation.isLoading ||
        deleteNoteMutation.isLoading;

    const showEditableForm = () => {
        return editMode || !selectedNoteId;
    }

    const getNoteList = () => {
        return (
            <NotesListWrapper
                availableNoteTypes={noteTypeList}
                availableNoteRetailSubjects={noteSubjectsRetailQuery.data}
                availableNoteAuthors={noteAuthorsQuery.data}
                salesPersonId={salesPersonId}
                intermediaryId={intermediaryId}
                selectedNoteId={selectedNoteId}
                isV360View={true}
                onAddNoteButtonClicked={() => {
                    setSelectedNoteId(undefined);
                }}
                onNoteSelected={(noteId, entityType, entityId) => {
                    setSelectedNoteId(noteId);
                    setEntitytype(entityType);
                    setEntityId(entityId);
                    if (noteId) {
                        setMobileFullScreenViewOpened(false);
                    }
                }}
            />
        );
    };

    const getNoteDetailsForm = () => {
        return (
            loadingInProgress
            ? <div style={{height: "100%", display: "flex", justifyContent: "center", alignItems: "center"}}>
                <Loader label={pl.common.appLoading} />
            </div>
            : showEditableForm()
            ? <NoteDetailsForm
                availableNoteTypes={noteTypeList}
                salesPersonId={salesPersonId}
                selectedNoteId={selectedNoteId}
                onNoteCreated={() => createNoteMutation.mutate()}
                onNoteUpdated={() => updateNoteMutation.mutate()}
                refetchNoteQuery={() => noteQuery.refetch}
                noteDetails={noteQuery.data}
                noteSubjectsRetailQuery={noteSubjectsRetailQuery.data}
                editMode={editMode}
                setEditMode={(bool) => setEditMode(bool)}
                loadingInProgress={loadingInProgress}
                entityName={entityName}
                editableNoteDetails={editableNoteDetails}
                intermediaryReference={intermediaryReference ?? []}
                setEditableNoteDetails={(data) => setEditableNoteDetails(data)}
            />
            : <NoteDetailsFormReadOnly
                isEditable={true}
                noteDetails={noteQuery.data}
                onEditClick={() => setEditMode(true)}
                onNoteDeleted={() => deleteNoteMutation.mutate()}
            />
        );
    };

    if (noteTypesQuery.isFetching) {
        return (
            <SegmentContent>
                <Loader label="Pobieranie konfiguracji notatek" />
            </SegmentContent>
        );
    }

    return (
        <NotesTabContent
            isMobile={isMobile}
            isEditNoteEnabled={true}
            notesList={getNoteList()}
            noteDetailsForm={getNoteDetailsForm()}
            deleteNote={() => deleteNoteMutation.mutate()}
            mobileFullScreenViewOpened={mobileFullScreenViewOpened}
            setMobileFullScreenViewOpened={(open) => setMobileFullScreenViewOpened(open)}
            selectedNoteId={selectedNoteId}
            setSelectedNoteId={(noteId) => setSelectedNoteId(noteId)}
            canNoteBeDeleted={noteQuery.data?.canBeDeleted}
            isInEditMode={editMode}
            setEditMode={(editMode) => setEditMode(editMode)}
        />
    )
};

export default View360NotesTabContent;
